import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LiteracyIntro from './components/LiteracyIntro';
import VocabTest from './components/VocabTest';
import ReadingTest from './components/ReadingTest';
import LiteracyResult from './components/LiteracyResult';
import TestResumeBanner from '@/components/feature/TestResumeBanner';
import { TEST_PROGRESS_KEYS, readProgress, writeProgress, clearProgress } from '@/lib/testProgress';
import { vocabularyQuestions } from '@/mocks/vocabularyQuestions';
import { readingQuestions } from '@/mocks/readingQuestions';

export type LiteracyStep = 'intro' | 'vocab' | 'reading' | 'result';

export interface LiteracyAnswers {
  vocab: Record<string, number>;
  reading: Record<string, number>;
}

interface LiteracyPageProgress {
  step: LiteracyStep;
  vocab: Record<string, number>;
  reading: Record<string, number>;
}

interface ResumeInfo {
  step: LiteracyStep;
  detail: string;
  vocab: Record<string, number>;
  reading: Record<string, number>;
}

export default function LiteracyTestPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<LiteracyStep>('intro');
  const [answers, setAnswers] = useState<LiteracyAnswers>({ vocab: {}, reading: {} });
  const [resume, setResume] = useState<ResumeInfo | null>(null);

  // 마운트 시 저장된 진행 상황 확인
  useEffect(() => {
    const page = readProgress<LiteracyPageProgress>(TEST_PROGRESS_KEYS.literacyPage);
    if (!page) return;

    if (page.step === 'reading') {
      const r = readProgress<{ current: number; answers: Record<string, number> }>(
        TEST_PROGRESS_KEYS.literacyReading,
      );
      const done = r ? Math.min(r.current + 1, readingQuestions.length) : 1;
      setResume({
        step: 'reading',
        detail: `문해력 ${done} / ${readingQuestions.length}문항 진행 중 (어휘력 완료)`,
        vocab: page.vocab || {},
        reading: page.reading || {},
      });
    } else if (page.step === 'vocab') {
      const v = readProgress<{ current: number; answers: Record<string, number> }>(
        TEST_PROGRESS_KEYS.literacyVocab,
      );
      if (v && (Object.keys(v.answers || {}).length > 0 || v.current > 0)) {
        const done = Math.min(v.current + 1, vocabularyQuestions.length);
        setResume({
          step: 'vocab',
          detail: `어휘력 ${done} / ${vocabularyQuestions.length}문항 진행 중`,
          vocab: page.vocab || {},
          reading: page.reading || {},
        });
      }
    }
  }, []);

  // 페이지 상태(단계 + 완료 답안) 자동 저장
  useEffect(() => {
    if (step === 'vocab' || step === 'reading') {
      writeProgress(TEST_PROGRESS_KEYS.literacyPage, {
        step,
        vocab: answers.vocab,
        reading: answers.reading,
      });
    }
  }, [step, answers]);

  const clearAll = () => {
    clearProgress(
      TEST_PROGRESS_KEYS.literacyPage,
      TEST_PROGRESS_KEYS.literacyVocab,
      TEST_PROGRESS_KEYS.literacyReading,
    );
    setResume(null);
  };

  const startFresh = () => {
    clearAll();
    setAnswers({ vocab: {}, reading: {} });
    setStep('vocab');
  };

  const resumeTest = () => {
    if (!resume) return;
    setAnswers({ vocab: resume.vocab || {}, reading: resume.reading || {} });
    setStep(resume.step);
    setResume(null);
  };

  const handleVocabComplete = (vocabAnswers: Record<string, number>) => {
    clearProgress(TEST_PROGRESS_KEYS.literacyVocab);
    setAnswers((prev) => ({ ...prev, vocab: vocabAnswers }));
    setStep('reading');
  };

  const handleReadingComplete = (readingAnswers: Record<string, number>) => {
    clearProgress(TEST_PROGRESS_KEYS.literacyReading);
    setAnswers((prev) => ({ ...prev, reading: readingAnswers }));
    setStep('result');
  };

  // 결과 도달 시 진행 상황 정리
  useEffect(() => {
    if (step === 'result') {
      clearProgress(
        TEST_PROGRESS_KEYS.literacyPage,
        TEST_PROGRESS_KEYS.literacyVocab,
        TEST_PROGRESS_KEYS.literacyReading,
      );
      setResume(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const retake = () => {
    clearAll();
    setAnswers({ vocab: {}, reading: {} });
    setStep('intro');
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top Nav */}
      <nav className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-book-read-line text-white text-xs"></i>
            </div>
            <span className="font-semibold text-foreground-950 text-sm">어휘력·문해력 검사</span>
          </div>

          {/* Progress */}
          {step !== 'intro' && step !== 'result' && (
            <div className="ml-auto flex items-center gap-2">
              {['vocab', 'reading'].map((s, idx) => (
                <div
                  key={s}
                  className={`w-7 h-1.5 rounded-full transition-colors ${
                    (step === 'vocab' && idx === 0) || (step === 'reading' && idx <= 1)
                      ? 'bg-primary-500'
                      : 'bg-background-200'
                  }`}
                ></div>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        {step === 'intro' && (
          <>
            {resume && (
              <div className="max-w-2xl mx-auto mb-6">
                <TestResumeBanner detail={resume.detail} onResume={resumeTest} onReset={clearAll} />
              </div>
            )}
            <LiteracyIntro onStart={startFresh} />
          </>
        )}
        {step === 'vocab' && <VocabTest onComplete={handleVocabComplete} />}
        {step === 'reading' && <ReadingTest onComplete={handleReadingComplete} />}
        {step === 'result' && <LiteracyResult answers={answers} onRetake={retake} />}
      </main>
    </div>
  );
}